import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "../../api/api";
import {
  ResponsiveContainer,
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  ReferenceLine,
} from "recharts";
import { FiFilter } from "react-icons/fi";
import { IoMdAdd } from "react-icons/io";
import SideNavLayout from "../../Sidenav/SideNavLayout";
import {
  EMR_HISTORY,
  COMPANY_STATS,
  COMPANY_KEYS,
  buildMetricByYear,
} from "./hseStatsData";

const Card = ({ children, className = "" }) => (
  <div
    className={`bg-white rounded-xl shadow-sm border border-gray-100 p-4 ${className}`}
  >
    {children}
  </div>
);

const SectionTitle = ({ title, subtitle }) => (
  <div className="text-left mb-3">
    <h3 className="text-base font-semibold text-gray-900">{title}</h3>
    {subtitle && <p className="text-xs text-gray-500">{subtitle}</p>}
  </div>
);

const KpiTile = ({ label, value, color, hint }) => (
  <div
    className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-left"
    style={{ borderLeft: `4px solid ${color}` }}
  >
    <div className="text-[11px] uppercase tracking-wider text-gray-500">{label}</div>
    <div className="mt-1 text-2xl font-bold text-[#0A0A0A]">{value}</div>
    {hint && <div className="text-[11px] text-gray-500 mt-0.5">{hint}</div>}
  </div>
);

const METRICS = [
  { key: "trir",       label: "TRIR (Total Recordable Incident Rate)", subtitle: "Lower is better" },
  { key: "recordable", label: "Recordable Injuries / Illnesses",       subtitle: "Total count per year" },
  { key: "lostTime",   label: "Lost-Time & Days-Away Cases",           subtitle: "Severity indicator" },
  { key: "fatalities", label: "Fatalities",                            subtitle: "Total count per year" },
  { key: "hours",      label: "Total Hours Worked",                    subtitle: "Exposure base" },
  { key: "employees",  label: "Average Number of Employees",           subtitle: "Headcount" },
];

// Embeddable inner content (no SideNavLayout wrapper) — used by the Safety
// Analytics "KPI's" tab. The exported HseStatsDashboard below just wraps it.
export const HseStatsContent = ({ embedded = false }) => {
  const [activeMetric, setActiveMetric] = useState("trir");
  const [selectedCompany, setSelectedCompany] = useState("");

  // ── User-entered overlay rows ─────────────────────────────────────────────
  // Loaded from /v1/hsestats/entries. Each row is { company_key, year, ... }.
  // We merge them into the static workbook series so any year a user updates
  // wins over the hard-coded value, and brand-new years are appended.
  const [overlay, setOverlay] = useState([]);
  const [overlayLoading, setOverlayLoading] = useState(false);
  const [showAddData, setShowAddData] = useState(false);

  const [emrOverlay, setEmrOverlay] = useState([]);
  const [showAddEmr, setShowAddEmr] = useState(false);

  const [showFilters, setShowFilters] = useState(false);
  const [showAddMenu, setShowAddMenu] = useState(false);
  const filterRef = useRef(null);
  const addRef = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target))
        setShowFilters(false);
      if (addRef.current && !addRef.current.contains(e.target))
        setShowAddMenu(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const fetchOverlay = async () => {
    try {
      setOverlayLoading(true);
      const resp = await api.get("/v1/hsestats/entries");
      const data = resp?.data?.data ?? resp?.data ?? [];
      setOverlay(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-fatal — KPI tab still renders the static workbook data.
      console.warn("HSE overlay fetch failed", e);
    } finally {
      setOverlayLoading(false);
    }
  };
  const fetchEmrOverlay = async () => {
    try {
      const resp = await api.get("/v1/hsestats/emr");
      const data = resp?.data?.data ?? resp?.data ?? [];
      setEmrOverlay(Array.isArray(data) ? data : []);
    } catch (e) {
      console.warn("EMR overlay fetch failed", e);
    }
  };
  useEffect(() => {
    fetchOverlay();
    fetchEmrOverlay();
  }, []);

  // Merge static EMR_HISTORY with user-entered EMR rows. Overlay wins per year.
  const mergedEmrHistory = useMemo(() => {
    const byYear = {};
    EMR_HISTORY.forEach((r) => { byYear[r.year] = { ...r }; });
    emrOverlay.forEach((o) => {
      byYear[o.year] = { year: o.year, rate: Number(o.rate) };
    });
    return Object.values(byYear).sort((a, b) => a.year - b.year);
  }, [emrOverlay]);

  // Merge static series + overlay rows for one company. Overlay wins per year.
  const mergedSeriesFor = (companyKey) => {
    const baseSeries = COMPANY_STATS[companyKey]?.series || [];
    const overlayRows = overlay.filter((o) => o.company_key === companyKey);
    if (!overlayRows.length) return baseSeries;
    const byYear = {};
    baseSeries.forEach((r) => {
      byYear[r.year] = { ...r };
    });
    overlayRows.forEach((o) => {
      const cur = byYear[o.year] || { year: o.year };
      // Map snake_case API → camelCase the chart code expects
      byYear[o.year] = {
        ...cur,
        year: o.year,
        recordable: o.recordable ?? cur.recordable ?? 0,
        lostTime: o.lost_time ?? cur.lostTime ?? 0,
        fatalities: o.fatalities ?? cur.fatalities ?? 0,
        hours: o.hours ?? cur.hours ?? 0,
        employees: o.employees ?? cur.employees ?? null,
        trir: o.trir ?? cur.trir ?? 0,
      };
    });
    // Sort newest year first to match the existing static data ordering.
    return Object.values(byYear).sort((a, b) => b.year - a.year);
  };

  // Year-range filter (from/to). Defaults to the full span available.
  const allYears = useMemo(() => {
    const set = new Set();
    Object.values(COMPANY_STATS).forEach((c) => {
      (c.series || []).forEach((r) => {
        if (r.year != null) set.add(Number(r.year));
      });
    });
    EMR_HISTORY.forEach((r) => {
      if (r.year != null) set.add(Number(r.year));
    });
    emrOverlay.forEach((r) => {
      if (r.year != null) set.add(Number(r.year));
    });
    return Array.from(set).sort((a, b) => a - b);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emrOverlay]);
  const minYear = allYears[0];
  const maxYear = allYears[allYears.length - 1];
  const [fromYear, setFromYear] = useState("");
  const [toYear, setToYear] = useState("");
  const effectiveFrom = Number(fromYear) || minYear;
  const effectiveTo = Number(toYear) || maxYear;
  const inRange = (y) => {
    const n = Number(y);
    if (!Number.isFinite(n)) return true;
    return n >= effectiveFrom && n <= effectiveTo;
  };
  const filterSeries = (series) =>
    (series || []).filter((r) => inRange(r.year));

  // Build the metric-switcher dataset from the merged series so that any
  // overlay edits (e.g. updating Constructors 2026 employees from 366 → 364)
  // flow through to the chart instead of reading the static workbook.
  const metricData = useMemo(() => {
    const years = new Set();
    COMPANY_KEYS.forEach((k) =>
      mergedSeriesFor(k).forEach((row) => {
        if (row.year != null) years.add(row.year);
      }),
    );
    return Array.from(years)
      .sort((a, b) => a - b)
      .filter((y) => inRange(y))
      .map((y) => {
        const obj = { year: y };
        COMPANY_KEYS.forEach((k) => {
          const found = mergedSeriesFor(k).find((r) => r.year === y);
          obj[k] = found ? found[activeMetric] ?? null : null;
        });
        return obj;
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeMetric, fromYear, toYear, overlay]);

  const filteredEmrHistory = useMemo(
    () => mergedEmrHistory.filter((r) => inRange(r.year)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [mergedEmrHistory, fromYear, toYear],
  );

  // Career totals — fall back to combined Operations when no company is picked
  const fallbackKey = selectedCompany || "OPERATIONS";
  const selectedCompanyCfg = COMPANY_STATS[fallbackKey];
  const companySeries = filterSeries(mergedSeriesFor(fallbackKey));
  const totalRecordable = companySeries.reduce((a, r) => a + (r.recordable || 0), 0);
  const totalLost = companySeries.reduce((a, r) => a + (r.lostTime || 0), 0);
  const totalFatalities = companySeries.reduce((a, r) => a + (r.fatalities || 0), 0);
  const totalHours = companySeries.reduce((a, r) => a + (r.hours || 0), 0);
  const totalAvgEmployees = (() => {
    const valid = companySeries.filter((r) => r.employees != null);
    if (!valid.length) return 0;
    return Math.round(
      valid.reduce((a, r) => a + r.employees, 0) / valid.length,
    );
  })();
  const latestEmr = filteredEmrHistory[filteredEmrHistory.length - 1] || mergedEmrHistory[mergedEmrHistory.length - 1];
  const previousEmr = filteredEmrHistory[filteredEmrHistory.length - 2] || mergedEmrHistory[mergedEmrHistory.length - 2];
  const emrTrend =
    previousEmr && latestEmr
      ? +(latestEmr.rate - previousEmr.rate).toFixed(2)
      : 0;
  const emrAvg = filteredEmrHistory.length
    ? filteredEmrHistory.reduce((a, r) => a + r.rate, 0) /
      filteredEmrHistory.length
    : 0;

  const activeMetricCfg = METRICS.find((m) => m.key === activeMetric);

  const renderCompanyCard = (k, opts = {}) => {
    const c = COMPANY_STATS[k];
    const cSeries = filterSeries(mergedSeriesFor(k));
    const totals = cSeries.reduce(
      (acc, r) => {
        acc.recordable += r.recordable || 0;
        acc.lostTime += r.lostTime || 0;
        acc.fatalities += r.fatalities || 0;
        acc.hours += r.hours || 0;
        return acc;
      },
      { recordable: 0, lostTime: 0, fatalities: 0, hours: 0 },
    );
    return (
      <Card
        key={k}
        className={`${opts.span || "lg:col-span-2"} ${
          opts.highlighted
            ? "ring-2 ring-offset-2"
            : ""
        }`}
        {...(opts.highlighted ? { style: { boxShadow: `0 0 0 2px ${c.color}33` } } : {})}
      >
        <div className="flex items-start justify-between flex-wrap gap-2 mb-2">
          <SectionTitle
            title={
              opts.highlighted ? `${c.label} — Selected` : c.label
            }
            subtitle={c.note || `${cSeries.length} years on record`}
          />
          <div className="flex gap-2 text-[11px]">
            <span className="px-2 py-1 rounded-full bg-orange-50 text-orange-700 font-semibold border border-orange-100">
              Recordable: {totals.recordable}
            </span>
            <span className="px-2 py-1 rounded-full bg-red-50 text-red-700 font-semibold border border-red-100">
              Lost-Time: {totals.lostTime}
            </span>
            <span className="px-2 py-1 rounded-full bg-purple-50 text-purple-700 font-semibold border border-purple-100">
              Hours: {Math.round(totals.hours).toLocaleString()}
            </span>
          </div>
        </div>
        <div style={{ height: 220 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart
              data={[...cSeries].reverse()}
              margin={{ left: 10, right: 20, top: 10, bottom: 20 }}
            >
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis dataKey="year" fontSize={11} />
              <YAxis fontSize={11} />
              <Tooltip
                formatter={(v) =>
                  typeof v === "number" ? v.toLocaleString() : v
                }
              />
              <Legend wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="recordable" name="Recordable" fill="#F59E0B" radius={[4, 4, 0, 0]} />
              <Bar dataKey="lostTime" name="Lost Time" fill="#DC2626" radius={[4, 4, 0, 0]} />
              <Bar dataKey="fatalities" name="Fatalities" fill="#0A0A0A" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>
    );
  };

  const Wrapper = embedded ? React.Fragment : SideNavLayout;
  const wrapperProps = embedded ? {} : {};

  return (
    <Wrapper {...wrapperProps}>
      <div
        className={
          embedded
            ? "flex flex-col"
            : "flex flex-col min-h-[calc(100vh-100px)] max-h-[calc(100vh-100px)] p-2 overflow-y-auto"
        }
        style={embedded ? {} : { scrollbarWidth: "none" }}
      >
        <div className="px-2 flex items-center justify-between flex-wrap mb-5">
          <div>
            <div className="font-inter font-bold text-[20px] text-left">HSE Performance Stats</div>
          </div>
          <div className="flex items-center gap-2">
            {/* ── Filter icon ──────────────────────────────── */}
            <div className="relative" ref={filterRef}>
              <button
                onClick={() => setShowFilters((p) => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md border transition-all ${
                  showFilters || selectedCompany || fromYear || toYear
                    ? "bg-gray-900 text-white border-gray-900"
                    : "bg-white text-gray-700 border-gray-300 hover:border-gray-500"
                }`}
                title="Filter by company and date range"
              >
                <FiFilter size={14} />
                <span>Filters</span>
                {(selectedCompany || fromYear || toYear) && (
                  <span className="ml-1 w-5 h-5 flex items-center justify-center rounded-full bg-white text-gray-900 text-[10px] font-bold leading-none">
                    {(selectedCompany ? 1 : 0) + (fromYear || toYear ? 1 : 0)}
                  </span>
                )}
              </button>

              {showFilters && (
                <div className="absolute right-0 top-full mt-2 w-80 bg-white rounded-xl shadow-xl border border-gray-200 z-50 p-4 space-y-4">
                  <div>
                    <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                      Company
                    </label>
                    <select
                      value={selectedCompany}
                      onChange={(e) => setSelectedCompany(e.target.value)}
                      className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-black focus:outline-none focus:border-gray-500"
                    >
                      <option value="">All companies</option>
                      {COMPANY_KEYS.map((k) => (
                        <option key={k} value={k}>
                          {COMPANY_STATS[k].label}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                        From
                      </label>
                      <select
                        value={fromYear}
                        onChange={(e) => setFromYear(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-black focus:outline-none focus:border-gray-500"
                      >
                        <option value="">All</option>
                        {allYears.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                        To
                      </label>
                      <select
                        value={toYear}
                        onChange={(e) => setToYear(e.target.value)}
                        className="w-full px-3 py-1.5 text-sm border border-gray-300 rounded-md bg-white text-black focus:outline-none focus:border-gray-500"
                      >
                        <option value="">All</option>
                        {allYears.map((y) => (
                          <option key={y} value={y}>{y}</option>
                        ))}
                      </select>
                    </div>
                  </div>

                  {(selectedCompany || fromYear || toYear) && (
                    <button
                      onClick={() => {
                        setSelectedCompany("");
                        setFromYear("");
                        setToYear("");
                      }}
                      className="w-full px-3 py-1.5 text-xs rounded-md border border-gray-200 hover:bg-gray-100 text-gray-600"
                    >
                      Clear all filters
                    </button>
                  )}
                </div>
              )}
            </div>

            {/* ── Add button with tabs ─────────────────────── */}
            <div className="relative" ref={addRef}>
              <button
                onClick={() => setShowAddMenu((p) => !p)}
                className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-md transition-all ${
                  showAddMenu
                    ? "bg-[#701313] text-white"
                    : "bg-[#8B1818] text-white hover:bg-[#701313]"
                }`}
                title="Add HSE data or EMR"
              >
                <IoMdAdd size={16} />
                <span>Add</span>
              </button>

              {showAddMenu && (
                <div className="absolute right-0 top-full mt-2 w-48 bg-white rounded-xl shadow-xl border border-gray-200 z-50 overflow-hidden">
                  <button
                    onClick={() => {
                      setShowAddMenu(false);
                      setShowAddData(true);
                    }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 border-b border-gray-100 font-medium text-gray-800"
                  >
                    Add Data
                    <span className="block text-[11px] font-normal text-gray-500">
                      HSE stats for a company / year
                    </span>
                  </button>
                  <button
                    onClick={() => {
                      setShowAddMenu(false);
                      setShowAddEmr(true);
                    }}
                    className="w-full text-left px-4 py-3 text-sm hover:bg-gray-50 font-medium text-gray-800"
                  >
                    Add EMR
                    <span className="block text-[11px] font-normal text-gray-500">
                      Experience Modification Rate
                    </span>
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-4 px-2">
          {/* KPI tiles */}
          <KpiTile
            label="Latest EMR"
            value={latestEmr.rate.toFixed(2)}
            color="#10B981"
            hint={
              emrTrend === 0
                ? "Flat vs prior year"
                : emrTrend < 0
                ? `↓ ${Math.abs(emrTrend).toFixed(2)} vs ${previousEmr.year}`
                : `↑ ${emrTrend.toFixed(2)} vs ${previousEmr.year}`
            }
          />
          <KpiTile
            label={
              companySeries.length
                ? `Total Hours (${companySeries[companySeries.length - 1].year}–${companySeries[0].year})`
                : "Total Hours"
            }
            value={Math.round(totalHours).toLocaleString()}
            color="#9333EA"
            hint={selectedCompany ? selectedCompanyCfg.label : "All Companies (combined)"}
          />
          <KpiTile
            label="Total Recordables (career)"
            value={totalRecordable}
            color="#F59E0B"
            hint={`${selectedCompany ? selectedCompanyCfg.label : "All Companies"} · ${companySeries.length} years`}
          />
          <KpiTile
            label="Fatalities (career)"
            value={totalFatalities}
            color="#DC2626"
            hint={`${selectedCompany ? selectedCompanyCfg.label : "All Companies"} · Lost-time: ${totalLost} · Avg HC: ${totalAvgEmployees}`}
          />

          {/* Selected company's detail card — only when one is picked */}
          {selectedCompany &&
            renderCompanyCard(selectedCompany, {
              span: "lg:col-span-4",
              highlighted: true,
            })}

          {/* EMR trend chart */}
          <Card className="lg:col-span-4">
            <div className="flex items-start justify-between flex-wrap gap-2 mb-1">
              <SectionTitle
                title="Experience Modification Rate (EMR)"
                subtitle="Insurance benchmark · 1.00 = industry average · lower is better"
              />
              <div className="flex gap-2 text-[11px]">
                <span className="px-2 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100">
                  Latest {latestEmr.year}: {latestEmr.rate.toFixed(2)}
                </span>
                <span className="px-2 py-1 rounded-full bg-indigo-50 text-indigo-700 font-semibold border border-indigo-100">
                  Avg {emrAvg.toFixed(2)}
                </span>
              </div>
            </div>
            <div style={{ height: 320 }}>
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={filteredEmrHistory}
                  margin={{ left: 10, right: 20, top: 10, bottom: 30 }}
                >
                  <defs>
                    <linearGradient id="emrGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.5} />
                      <stop offset="100%" stopColor="#10B981" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="year"
                    fontSize={11}
                    label={{ value: "Year", position: "insideBottom", offset: -8, fontSize: 11, fill: "#6B7280" }}
                  />
                  <YAxis
                    fontSize={11}
                    domain={[0, 1.2]}
                    label={{ value: "EMR", angle: -90, position: "insideLeft", offset: 10, fontSize: 11, fill: "#6B7280" }}
                  />
                  <Tooltip />
                  <ReferenceLine y={1} stroke="#DC2626" strokeDasharray="4 4" label={{ value: "Industry Avg (1.00)", fontSize: 10, fill: "#DC2626", position: "right" }} />
                  <Area
                    type="monotone"
                    dataKey="rate"
                    stroke="#10B981"
                    strokeWidth={2}
                    fill="url(#emrGrad)"
                    dot={{ r: 3 }}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Metric switcher */}
          <Card className="lg:col-span-4">
            <div className="flex items-start justify-between flex-wrap gap-3 mb-2">
              <SectionTitle
                title={`Per-Company — ${activeMetricCfg.label}`}
                subtitle={activeMetricCfg.subtitle}
              />
              <div className="flex flex-wrap gap-2">
                {METRICS.map((m) => (
                  <button
                    key={m.key}
                    onClick={() => setActiveMetric(m.key)}
                    className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-all ${
                      activeMetric === m.key
                        ? "bg-gray-900 text-white border-gray-900"
                        : "bg-white text-gray-600 border-gray-200 hover:border-gray-400"
                    }`}
                  >
                    {m.label}
                  </button>
                ))}
              </div>
            </div>
            <div style={{ height: 380 }}>
              <ResponsiveContainer width="100%" height="100%">
                {activeMetric === "trir" ? (
                  <LineChart
                    data={metricData}
                    margin={{ left: 10, right: 20, top: 10, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {COMPANY_KEYS.map((k) => (
                      <Line
                        key={k}
                        type="monotone"
                        dataKey={k}
                        name={COMPANY_STATS[k].label}
                        stroke={COMPANY_STATS[k].color}
                        strokeWidth={2}
                        connectNulls
                        dot={{ r: 3 }}
                      />
                    ))}
                  </LineChart>
                ) : (
                  <BarChart
                    data={metricData}
                    margin={{ left: 10, right: 20, top: 10, bottom: 30 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey="year" fontSize={11} />
                    <YAxis fontSize={11} />
                    <Tooltip
                      formatter={(v) =>
                        typeof v === "number" ? v.toLocaleString() : v
                      }
                    />
                    <Legend wrapperStyle={{ fontSize: 11 }} />
                    {COMPANY_KEYS.map((k) => (
                      <Bar
                        key={k}
                        dataKey={k}
                        name={COMPANY_STATS[k].label}
                        fill={COMPANY_STATS[k].color}
                        radius={[4, 4, 0, 0]}
                      />
                    ))}
                  </BarChart>
                )}
              </ResponsiveContainer>
            </div>
          </Card>

          {/* Per-Company breakdown — skip the one already shown above (if any) */}
          {COMPANY_KEYS.filter(
            (k) => !selectedCompany || k !== selectedCompany,
          ).map((k) => renderCompanyCard(k))}
        </div>
      </div>

      {showAddData && (
        <AddHseDataModal
          allYears={allYears}
          overlay={overlay}
          onClose={() => setShowAddData(false)}
          onSaved={() => {
            setShowAddData(false);
            fetchOverlay();
          }}
        />
      )}
      {showAddEmr && (
        <AddEmrModal
          allYears={allYears}
          mergedEmrHistory={mergedEmrHistory}
          onClose={() => setShowAddEmr(false)}
          onSaved={() => {
            setShowAddEmr(false);
            fetchEmrOverlay();
          }}
        />
      )}
    </Wrapper>
  );
};

// ── "Add EMR" modal ─────────────────────────────────────────────────────────
const AddEmrModal = ({ allYears, mergedEmrHistory, onClose, onSaved }) => {
  const [year, setYear] = useState("");
  const [rate, setRate] = useState("");
  const [notes, setNotes] = useState("");
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Pre-fill rate when the year already has a value
  useEffect(() => {
    if (!year) return;
    const yr = Number(year);
    const existing = mergedEmrHistory.find((r) => r.year === yr);
    if (existing) {
      setRate(existing.rate ?? "");
    } else {
      setRate("");
    }
  }, [year, mergedEmrHistory]);

  const yearOptions = useMemo(() => {
    const set = new Set(allYears);
    set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [allYears]);

  const save = async () => {
    setMsg("");
    if (!year) return setMsg("Pick a year.");
    const r = Number(rate);
    if (!Number.isFinite(r)) return setMsg("Rate must be a number (e.g. 0.65).");
    if (r < 0 || r > 5) return setMsg("Rate must be between 0 and 5.");
    setSaving(true);
    try {
      const resp = await api.post("/v1/hsestats/emr", {
        year: Number(year),
        rate: r,
        notes: notes || null,
      });
      if (resp.status === 200) {
        onSaved();
      } else {
        setMsg(resp?.message || "Save failed.");
      }
    } catch (e) {
      setMsg(e?.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-md max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">Add / Update EMR</h3>
            {/* <p className="text-xs text-gray-500">
              1.00 = industry average · lower is better.
            </p> */}
          </div>
          <button onClick={onClose} className="text-gray-500 hover:text-gray-800">✕</button>
        </div>

        <div className="p-5 space-y-4">
          <div>
            <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Year <span className="text-red-500">*</span>
            </label>
            <select
              value={year}
              onChange={(e) => setYear(e.target.value)}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
            >
              <option value="">Select year…</option>
              {yearOptions.map((y) => (
                <option key={y} value={y}>{y}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Rate <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              step="0.01"
              min={0}
              max={5}
              value={rate}
              onChange={(e) => setRate(e.target.value)}
              placeholder="e.g. 0.65"
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
            />
            {/* <p className="text-[11px] text-gray-500 mt-1">
              Typical range: 0.50 – 1.50. Most clients accept ≤ 1.00.
            </p> */}
          </div>

          {/* <div>
            <label className="block text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
              Notes
            </label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
              placeholder="Optional context (insurance carrier, effective date, etc.)"
            />
          </div> */}

          {msg && <div className="text-sm text-red-700">{msg}</div>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-1.5 text-sm font-medium rounded-md bg-emerald-700 text-white hover:bg-emerald-800 disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save EMR"}
          </button>
        </div>
      </div>
    </div>
  );
};

// ── "Add Data" modal ────────────────────────────────────────────────────────
const AddHseDataModal = ({ allYears, overlay, onClose, onSaved }) => {
  const [companyKey, setCompanyKey] = useState("");
  const [year, setYear] = useState("");
  const [form, setForm] = useState({
    recordable: "",
    lost_time: "",
    fatalities: "",
    hours: "",
    employees: "",
    trir: "",
    emr: "",
    notes: "",
  });
  const [saving, setSaving] = useState(false);
  const [msg, setMsg] = useState("");

  // Pre-fill from existing overlay row OR the static workbook value
  useEffect(() => {
    if (!companyKey || !year) return;
    const yr = Number(year);
    const existing = overlay.find(
      (o) => o.company_key === companyKey && o.year === yr,
    );
    const baseRow = COMPANY_STATS[companyKey]?.series.find((r) => r.year === yr);
    setForm({
      recordable: existing?.recordable ?? baseRow?.recordable ?? "",
      lost_time:  existing?.lost_time  ?? baseRow?.lostTime    ?? "",
      fatalities: existing?.fatalities ?? baseRow?.fatalities  ?? "",
      hours:      existing?.hours      ?? baseRow?.hours       ?? "",
      employees:  existing?.employees  ?? baseRow?.employees   ?? "",
      trir:       existing?.trir       ?? baseRow?.trir        ?? "",
      emr:        existing?.emr        ?? "",
      notes:      existing?.notes      ?? "",
    });
  }, [companyKey, year, overlay]);

  const numOrNull = (v) => {
    if (v === "" || v === null || v === undefined) return null;
    const n = Number(v);
    return Number.isFinite(n) ? n : null;
  };

  const save = async () => {
    setMsg("");
    if (!companyKey) return setMsg("Pick a company.");
    if (!year || !Number.isFinite(Number(year))) return setMsg("Pick a year.");
    setSaving(true);
    try {
      const resp = await api.post("/v1/hsestats/entries", {
        company_key: companyKey,
        year: Number(year),
        recordable: numOrNull(form.recordable),
        lost_time:  numOrNull(form.lost_time),
        fatalities: numOrNull(form.fatalities),
        hours:      numOrNull(form.hours),
        employees:  numOrNull(form.employees),
        trir:       numOrNull(form.trir),
        emr:        numOrNull(form.emr),
        notes:      form.notes || null,
      });
      if (resp.status === 200) {
        onSaved();
      } else {
        setMsg(resp?.message || "Save failed.");
      }
    } catch (e) {
      setMsg(e?.response?.data?.message || "Save failed.");
    } finally {
      setSaving(false);
    }
  };

  // Pre-set the year list to existing years + the current year if missing
  const yearOptions = useMemo(() => {
    const set = new Set(allYears);
    set.add(new Date().getFullYear());
    return Array.from(set).sort((a, b) => b - a);
  }, [allYears]);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-2xl max-h-[92vh] overflow-y-auto">
        <div className="flex items-center justify-between px-5 py-3 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold text-left text-gray-900">Add / Update HSE Data</h3>
            {/* <p className="text-xs text-gray-500">
              Saved values overlay the workbook history for that company &amp; year.
            </p> */}
          </div>
          <button
            onClick={onClose}
            className="text-gray-500 hover:text-gray-800"
          >
            ✕
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                Company <span className="text-red-500">*</span>
              </label>
              <select
                value={companyKey}
                onChange={(e) => setCompanyKey(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
              >
                <option value="">Select company…</option>
                {COMPANY_KEYS.map((k) => (
                  <option key={k} value={k}>
                    {COMPANY_STATS[k].label}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                Year <span className="text-red-500">*</span>
              </label>
              <select
                value={year}
                onChange={(e) => setYear(e.target.value)}
                className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
              >
                <option value="">Select year…</option>
                {yearOptions.map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[
              { k: "hours",      label: "Total Hours Worked" },
              { k: "employees",  label: "Average Number of Employees" },
              { k: "recordable", label: "Number of Total Recordable Injuries/Illnesses" },
              { k: "lost_time",  label: "Lost Workday Cases (LWC)" },
              { k: "fatalities", label: "Total Fatalities" },
              { k: "trir",       label: "Total Recordable Incident Rate (TRIR)" },
            ].map((f) => (
              <div key={f.k}>
                <label className="block text-left text-xs font-semibold text-gray-600 uppercase tracking-wider mb-1">
                  {f.label}
                </label>
                <input
                  type="number"
                  step="any"
                  min={0}
                  value={form[f.k]}
                  onChange={(e) =>
                    setForm((s) => ({ ...s, [f.k]: e.target.value }))
                  }
                  className="w-full border border-gray-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:border-gray-500"
                />
              </div>
            ))}
          </div>

          {msg && <div className="text-sm text-red-700">{msg}</div>}
        </div>

        <div className="flex justify-end gap-2 px-5 py-3 border-t border-gray-200 bg-gray-50 sticky bottom-0">
          <button
            onClick={onClose}
            className="px-4 py-1.5 text-sm font-medium rounded-md border border-gray-300 bg-white hover:bg-gray-100"
          >
            Cancel
          </button>
          <button
            onClick={save}
            disabled={saving}
            className="px-4 py-1.5 text-sm font-medium rounded-md bg-[#8B1818] text-white hover:bg-[#701313] disabled:opacity-60"
          >
            {saving ? "Saving…" : "Save Data"}
          </button>
        </div>
      </div>
    </div>
  );
};

const HseStatsDashboard = () => <HseStatsContent embedded={false} />;

export default HseStatsDashboard;
